"use client";
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, ArrowRight, ArrowLeft, Shield, Loader2 } from 'lucide-react';
import { useAuth } from './context/AuthContext'; // ✅ import useAuth

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { forgotPassword } = useAuth(); // ✅ pull from context

  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const validateEmail = () => {
    const newErrors = {};
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmail()) return;

    setIsLoading(true);
    setErrors({});

  const result = await forgotPassword(email.trim());

if (result.success) {
  localStorage.setItem("reset_email", email.trim());
  navigate('/dashboard/validate-otp', { state: { email: email.trim() } });
} else {
  setErrors({ general: result.message });
}

setIsLoading(false);

  };

  return (
  
    <div className="min-h-screen bg-gradient-to-br from-orange-200 via-orange-100 to-orange-50 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-teal-200 rounded-full opacity-20 animate-float"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-orange-300 rounded-full opacity-30 animate-float-delayed"></div>
        <div className="absolute bottom-32 left-1/4 w-40 h-40 bg-teal-100 rounded-full opacity-25 animate-float"></div>
        <div className="absolute bottom-20 right-10 w-28 h-28 bg-orange-200 rounded-full opacity-20 animate-float-delayed"></div>
      </div>

      {/* Main Container */}
      <div className="relative w-full max-w-md z-10">
        {/* Logo/Brand Section */}
        <div className="text-center mb-8">
         <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-teal-600 to-teal-800 rounded-2xl shadow-lg mb-4 transform hover:scale-105 transition-transform duration-300">
  <img
    src="https://cdn.acsdev.in/ManaPrint/manaprint_logo_tras.png"
    alt="ManaPrint Logo"
    className="w-10 h-10 object-contain"
  />
</div>
          <h1 className="text-3xl font-bold text-teal-800 mb-2">Forgot Password?</h1>
          <p className="text-gray-600">No worries — we'll send you an OTP to verify</p>
        </div>

        {/* Card */}
        <div className="bg-white bg-opacity-80 backdrop-blur-lg rounded-2xl shadow-2xl border border-white border-opacity-50 p-8 transform hover:scale-[1.02] transition-all duration-300">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* General Error */}
            {errors.general && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm animate-shake">
                {errors.general}
              </div>
            )}

            {/* Email Field */}
            <div className="space-y-2">
              <label htmlFor="email" className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <Mail className="w-4 h-4 text-teal-600" />
                Admin Email
              </label>
              <div className="relative">
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors((prev) => ({ ...prev, email: '' }));
                  }}
                  placeholder="Enter your admin email"
                  className={`w-full px-4 py-3 pl-12 bg-white bg-opacity-70 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-300 ${
                    errors.email
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300'
                  }`}
                  disabled={isLoading}
                />
                <Mail
                  className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                    errors.email ? 'text-red-400' : 'text-gray-400'
                  }`}
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-sm animate-fade-in">{errors.email}</p>
              )}
            </div>

            {/* Helper text */}
            <p className="text-xs text-gray-500 -mt-2">
              Enter the email address associated with your admin account. We'll send a one-time OTP to verify your identity.
            </p>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-teal-600 to-teal-800 text-white py-3 px-6 rounded-xl font-semibold hover:from-teal-700 hover:to-teal-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transform hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending OTP...
                </>
              ) : (
                <>
                  Send OTP
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>

            {/* Back to Login */}
            <Link
              to="/dashboard/login"
              className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all duration-300"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Link>
          </form>
        </div>

        {/* Security Notice */}
        <div className="mt-4 p-4 bg-white bg-opacity-50 backdrop-blur-sm rounded-xl border border-white border-opacity-60">
          <div className="flex items-start gap-3">
            <Shield className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-gray-600">
              <span className="font-semibold text-gray-700">Secure Reset — </span>
              OTPs expire after 10 minutes and can only be used once.
            </p>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-float-delayed { animation: float-delayed 8s ease-in-out infinite; }
        .animate-shake { animation: shake 0.5s ease-in-out; }
        .animate-fade-in { animation: fade-in 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default ForgotPassword;